/**
 * The centred card every signed-out page sits in: logo, a title, the body.
 * Login and Register predate this and lay theirs out by hand; the forgot /
 * reset / verify / Google pages share this one.
 */
import type { ReactNode } from 'react';
import BetaBadge from './BetaBadge';
import Logo from './Logo';
import LegalLinks from './LegalLinks';

export const INPUT_CLASS =
  'mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500';

export const PRIMARY_BUTTON_CLASS =
  'w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed';

export const LABEL_CLASS = 'block text-sm font-medium text-gray-700 dark:text-gray-300';

const TONE_CLASS = {
  success:
    'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 px-4 py-3 rounded',
  notice:
    'bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 px-4 py-3 rounded',
  error:
    'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded',
};

export function AuthMessage({
  tone,
  children,
}: {
  tone: keyof typeof TONE_CLASS;
  children: ReactNode;
}) {
  return <div className={TONE_CLASS[tone]}>{children}</div>;
}

export default function AuthCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-950">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-md p-8">
        <div className="mb-8 flex flex-col items-center gap-3">
          <Logo size={56} />
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-gray-100">
            {title}
            <BetaBadge />
          </h1>
        </div>
        <div className="space-y-6">
          {children}
          <LegalLinks />
        </div>
      </div>
    </div>
  );
}
